import { useEffect, useState } from "react";
import { ChevronRight } from "lucide-react";
import { Button, ErrorNote, Field, SidePanel, inputClass, useCanWrite, useIsAdmin } from "@/components/ui";
import { DeleteForever } from "@/components/delete";
import { FusoLetto, SiblingProposalPanel, SymbolPanel, SymbolsTable, symbolStatus, usePeersOf } from "@/components/symbols";
import { ApiError, api, errorMessage, type ServerPatch } from "@/lib/api";
import { useBrokers, useDebounced, usePropFirms, useRefreshDirectory, useServers } from "@/lib/queries";
import type { BrokerServer, ServerChangePreview, ServerKind, SymbolMapping } from "@/lib/types";

/*
 * I SERVER, VISTI DAL LORO POSSESSORE.
 *
 * Un server MT5 è di una prop firm o di un broker (`BrokerServer.propFirmId`):
 * si guarda dalla pagina di chi lo possiede, non da un elenco a parte. Questo
 * file è la parte che le due pagine (Prop firm, Broker) hanno in comune.
 */

/**
 * I server divisi per possessore, e quelli senza un possessore che si veda.
 *
 * `owners` sono i possessori MOSTRATI dalla pagina (gli attivi). Un server è
 * «senza possessore» se non ne ha, o se il suo possessore non è fra quelli: una
 * firm o un broker archiviato lascia i suoi server nel database, e senza questo
 * confronto sparirebbero da entrambe le pagine pur restando scelti nei conti.
 */
export function useServersByOwner(kind: ServerKind, owners: { id: string }[] | undefined) {
  const servers = useServers();
  const ofKind = (servers.data ?? []).filter((s) => s.kind === kind);
  const shown = new Set((owners ?? []).map((o) => o.id));
  const byOwner = new Map<string, BrokerServer[]>();
  const orphans: BrokerServer[] = [];
  for (const s of ofKind) {
    if (!s.propFirmId || !shown.has(s.propFirmId)) orphans.push(s);
    else byOwner.set(s.propFirmId, [...(byOwner.get(s.propFirmId) ?? []), s]);
  }
  /** Finché non sono letti (server E possessori), i server non sono «zero»: sono sconosciuti. */
  const ready = !servers.isLoading && !servers.error && owners !== undefined;
  return { byOwner, orphans, ready, error: servers.error };
}

/** «3 server · 5 conti», per la riga del possessore chiuso. */
export function serversSummary(servers: BrokerServer[]): string {
  const conti = servers.reduce((n, s) => n + s._count.accounts, 0);
  const s = servers.length === 1 ? "1 server" : `${servers.length} server`;
  return `${s} · ${conti} ${conti === 1 ? "conto" : "conti"}`;
}

/** Il pezzo cliccabile dell'intestazione di un possessore: apre e chiude. */
export function OwnerToggle({
  open,
  onToggle,
  title,
  summary,
}: {
  open: boolean;
  onToggle: () => void;
  title: string;
  summary: string;
}) {
  return (
    <button type="button" onClick={onToggle} aria-expanded={open} className="flex min-w-0 flex-1 items-center gap-2 text-left">
      <ChevronRight className={`h-4 w-4 shrink-0 text-faint transition-transform ${open ? "rotate-90" : ""}`} />
      <span className="min-w-0">
        <span className="block font-medium text-ink">{title}</span>
        <span className="block text-xs text-faint">{summary}</span>
      </span>
    </button>
  );
}

/** I server di UN possessore, con «Aggiungi server» che nasce legato a lui. */
export function OwnerServers({
  ownerId,
  ownerName,
  kind,
  servers,
  error,
}: {
  ownerId: string;
  ownerName: string;
  kind: ServerKind;
  /** `null` = non ancora letti: non si dice «nessun server». */
  servers: BrokerServer[] | null;
  error?: unknown;
}) {
  const canWrite = useCanWrite();
  const [adding, setAdding] = useState(false);
  // Chiusi all'inizio, e lo stato non si salva: ogni visita riparte chiusa.
  const [open, setOpen] = useState(false);

  return (
    <div className="border-t border-line">
      <div className="flex items-center justify-between gap-3 px-4 py-2">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-faint hover:text-muted"
        >
          <ChevronRight className={`h-3.5 w-3.5 shrink-0 transition-transform ${open ? "rotate-90" : ""}`} />
          {/* Finche' non sono letti non si dice un numero: non lo sappiamo. */}
          Server{servers === null ? "" : ` (${servers.length})`}
        </button>
        {canWrite && (
          <Button size="sm" onClick={() => setAdding(true)}>
            Aggiungi server
          </Button>
        )}
      </div>
      {!open ? null : servers === null ? (
        error ? (
          <div className="px-4 pb-4">
            <ErrorNote>{errorMessage(error)}</ErrorNote>
          </div>
        ) : (
          <p className="px-4 pb-4 text-sm text-muted">Caricamento…</p>
        )
      ) : servers.length === 0 ? (
        <p className="px-4 pb-4 text-sm text-muted">
          Nessun server di {ownerName}. Aggiungi quello del conto: si verifica sul gateway prima di salvarlo, e nasce legato a{" "}
          {kind === "PROP" ? "questa firm" : "questo broker"}.
        </p>
      ) : (
        <ServerRows servers={servers} kind={kind} withSiblings />
      )}
      {adding && <AddServerPanel ownerId={ownerId} ownerName={ownerName} kind={kind} onClose={() => setAdding(false)} />}
    </div>
  );
}

/** I server nati senza possessore: non spariscono con la pagina Server. */
export function OrphanServers({ kind, servers }: { kind: ServerKind; servers: BrokerServer[] }) {
  const [open, setOpen] = useState(false);
  if (servers.length === 0) return null;
  const isProp = kind === "PROP";
  return (
    <section className="card">
      <header className="flex items-center justify-between gap-3 px-4 py-3">
        <OwnerToggle
          open={open}
          onToggle={() => setOpen((o) => !o)}
          title="Server senza possessore"
          summary={serversSummary(servers)}
        />
      </header>
      {open && (
        <>
          <p className="border-t border-line px-4 py-2 text-xs text-faint">
            Server senza {isProp ? "una prop firm" : "un broker"} attivo: nati dal pannello «Nuovo conto» senza indicarlo, o
            di {isProp ? "una firm" : "un broker"} poi archiviato. I conti che li usano funzionano come gli altri. «Modifica»
            li lega a {isProp ? "una prop firm" : "un broker"} attivo.
          </p>
          <ServerRows
            servers={servers}
            kind={kind}
            withSiblings={false}
            caption={(s) => (s.propFirm ? `Possessore archiviato: ${s.propFirm.name}` : "Nessun possessore")}
          />
        </>
      )}
    </section>
  );
}

/** Le righe dei server, con i pannelli dei simboli che si aprono da lì. */
function ServerRows({
  servers,
  kind,
  withSiblings,
  caption,
}: {
  servers: BrokerServer[];
  kind: ServerKind;
  withSiblings: boolean;
  caption?: (s: BrokerServer) => string;
}) {
  const canWrite = useCanWrite();
  const isAdmin = useIsAdmin();
  const peersOf = usePeersOf();
  const [editing, setEditing] = useState<BrokerServer | null>(null);
  const [symbolPanel, setSymbolPanel] = useState<{ server: BrokerServer; symbol: SymbolMapping | null; canonical?: string } | null>(null);
  const [proposal, setProposal] = useState<BrokerServer | null>(null);

  // I fratelli sono i server dello stesso possessore che hanno già qualcosa da proporre.
  const siblingsOf = (srv: BrokerServer) => (withSiblings ? servers.filter((s) => s.id !== srv.id && s.symbols.length > 0) : []);

  return (
    <>
      {servers.map((srv) => (
        <ServerBlock
          key={srv.id}
          server={srv}
          canWrite={canWrite}
          isAdmin={isAdmin}
          peersOf={peersOf}
          hasSiblings={siblingsOf(srv).length > 0}
          caption={caption?.(srv)}
          onEditServer={() => setEditing(srv)}
          onAdd={(canonical) => setSymbolPanel({ server: srv, symbol: null, canonical })}
          onEdit={(symbol) => setSymbolPanel({ server: srv, symbol })}
          onPropose={() => setProposal(srv)}
        />
      ))}
      {symbolPanel && (
        <SymbolPanel
          server={symbolPanel.server}
          symbol={symbolPanel.symbol}
          canonical={symbolPanel.canonical}
          peersOf={peersOf}
          onClose={() => setSymbolPanel(null)}
        />
      )}
      {proposal && <SiblingProposalPanel server={proposal} siblings={siblingsOf(proposal)} onClose={() => setProposal(null)} />}
      {editing && <EditServerPanel server={editing} kind={kind} onClose={() => setEditing(null)} />}
    </>
  );
}

function ServerBlock({
  server,
  canWrite,
  isAdmin,
  peersOf,
  hasSiblings,
  caption,
  onEditServer,
  onAdd,
  onEdit,
  onPropose,
}: {
  server: BrokerServer;
  canWrite: boolean;
  isAdmin: boolean;
  peersOf: (serverId: string, canonical: string) => string[];
  hasSiblings: boolean;
  caption?: string;
  onEditServer: () => void;
  onAdd: (canonical?: string) => void;
  onEdit: (s: SymbolMapping) => void;
  onPropose: () => void;
}) {
  // L'elenco dei simboli non si vede finché non lo si apre.
  const [open, setOpen] = useState(false);

  return (
    <div className="border-t border-line">
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 pt-3">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <h4 className="text-sm font-semibold text-ink">{server.displayName ?? server.name}</h4>
          <FusoLetto server={server} />
          <span className="text-sm text-faint">
            {server._count.accounts} {server._count.accounts === 1 ? "conto" : "conti"}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
            {open ? "Nascondi i simboli" : "Vedi i simboli"}
          </Button>
          {canWrite && (
            <Button size="sm" variant="ghost" onClick={onEditServer}>
              Modifica
            </Button>
          )}
          {/*
           * Solo ADMIN: un server nato per errore si toglie di mezzo
           * insieme alle sue mappature, e non e' un gesto da lasciare a chi
           * lo aggiunge tutti i giorni.
           */}
          {isAdmin && <DeleteForever kind="server" id={server.id} label={server.displayName ?? server.name} size="sm" />}
        </div>
      </div>
      <p className="px-4 pb-3 pt-1 text-xs text-muted">
        {caption && <span className="text-warn">{caption} · </span>}
        {symbolStatus(server)}
      </p>
      {open && (
        <SymbolsTable server={server} canWrite={canWrite} peersOf={peersOf} hasSiblings={hasSiblings} onAdd={onAdd} onEdit={onEdit} onPropose={onPropose} />
      )}
    </div>
  );
}

/**
 * Un server nuovo, legato a chi lo possiede.
 *
 * Il tipo lo dà la pagina e non si sceglie, e il possessore nemmeno: nasce già
 * suo. La verifica sul gateway è del backend (`POST /servers`, senza
 * credenziali): se il nome non c'è, si propongono i più simili.
 */
function AddServerPanel({ ownerId, ownerName, kind, onClose }: { ownerId: string; ownerName: string; kind: ServerKind; onClose: () => void }) {
  const refresh = useRefreshDirectory();
  const [name, setName] = useState("");
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setName("");
    setSuggestions([]);
    setError(null);
  }, [ownerId]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setSuggestions([]);
    try {
      await api.servers.create({ name: name.trim(), kind, propFirmId: ownerId });
      await refresh();
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.code === "SERVER_NOT_ON_GATEWAY") setSuggestions(err.suggestions ?? []);
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <SidePanel
      open
      onClose={onClose}
      title="Aggiungi server"
      subtitle={`${ownerName} · ${kind === "PROP" ? "server prop" : "server broker"}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button variant="primary" type="submit" form="owner-server-form" disabled={busy}>
            {busy ? "Verifica…" : "Verifica e aggiungi"}
          </Button>
        </>
      }
    >
      <form id="owner-server-form" onSubmit={submit} className="space-y-4">
        <Field label="Nome esatto del server MT5" hint="Come compare nel terminale, per esempio FundedNext-Server 2. Si verifica col gateway prima di salvare.">
          {(id) => (
            <input
              id={id}
              required
              autoFocus
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setSuggestions([]);
              }}
              className={inputClass}
            />
          )}
        </Field>
        <NameSuggestions
          names={suggestions}
          onPick={(s) => {
            setName(s);
            setSuggestions([]);
            setError(null);
          }}
        />
        <ErrorNote>{error}</ErrorNote>
      </form>
    </SidePanel>
  );
}

/** I nomi simili che il gateway propone quando il nome scritto non c'è: cliccabili, riempiono il campo. */
function NameSuggestions({ names, onPick }: { names: string[]; onPick: (name: string) => void }) {
  if (names.length === 0) return null;
  return (
    <div className="space-y-1">
      <p className="text-xs text-muted">Non trovato. Forse uno di questi:</p>
      <div className="flex flex-wrap gap-2">
        {names.map((s) => (
          <button key={s} type="button" className="rounded border border-line px-2 py-1 text-xs text-ink hover:bg-raised" onClick={() => onPick(s)}>
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Corregge un server: nome, nome visualizzato, possessore.
 *
 * Il nome è quello da cui dipende se i conti si connettono, e per questo il
 * backend lo riverifica sul gateway e dice PRIMA quante connessioni rompe
 * (`change-preview`): l'anteprima si legge mentre si scrive, non dopo. Il tipo e
 * il fuso non si cambiano. Il possessore si sceglie fra quelli attivi dello
 * stesso tipo; un possessore archiviato si può solo lasciare com'è.
 */
function EditServerPanel({ server, kind, onClose }: { server: BrokerServer; kind: ServerKind; onClose: () => void }) {
  const refresh = useRefreshDirectory();
  const firms = usePropFirms();
  const brokers = useBrokers();
  const owners = (kind === "PROP" ? firms.data : brokers.data) ?? [];
  const [name, setName] = useState(server.name);
  const [displayName, setDisplayName] = useState(server.displayName ?? "");
  const [ownerId, setOwnerId] = useState(server.propFirmId ?? "");
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const newName = name.trim();
  const nameChanged = newName !== "" && newName !== server.name;
  const settled = useDebounced(newName, 400);
  const [preview, setPreview] = useState<{ for: string; data: ServerChangePreview | null } | null>(null);

  useEffect(() => {
    if (settled === "" || settled === server.name) {
      setPreview(null);
      return;
    }
    let stale = false;
    api.servers
      .changePreview(server.id, settled)
      .then((data) => !stale && setPreview({ for: settled, data }))
      // Anteprima non letta: si dice, non si lascia credere che non ci siano conseguenze.
      .catch(() => !stale && setPreview({ for: settled, data: null }));
    return () => {
      stale = true;
    };
  }, [settled, server.id, server.name]);

  const shownPreview = nameChanged && preview?.for === newName ? preview : null;
  const blockers = shownPreview?.data?.blockers ?? [];

  const body: ServerPatch = {
    ...(nameChanged ? { name: newName } : {}),
    ...(displayName.trim() !== (server.displayName ?? "") ? { displayName: displayName.trim() || null } : {}),
    ...(ownerId !== (server.propFirmId ?? "") ? { propFirmId: ownerId || null } : {}),
  };
  const changed = Object.keys(body).length > 0;
  const ownerArchived = !!server.propFirmId && !owners.some((o) => o.id === server.propFirmId);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!changed) return;
    setBusy(true);
    setError(null);
    setSuggestions([]);
    try {
      await api.servers.update(server.id, body);
      await refresh();
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.code === "SERVER_NOT_ON_GATEWAY") setSuggestions(err.suggestions ?? []);
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <SidePanel
      open
      onClose={onClose}
      title="Modifica server"
      subtitle={`${server.displayName ?? server.name} · ${kind === "PROP" ? "server prop" : "server broker"}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button variant="primary" type="submit" form="edit-server-form" disabled={busy || !changed || blockers.length > 0}>
            {busy ? "Salvataggio…" : "Salva"}
          </Button>
        </>
      }
    >
      <form id="edit-server-form" onSubmit={submit} className="space-y-4">
        <Field
          label="Nome esatto del server MT5"
          hint="Come compare nel terminale. Se lo cambi si riverifica sul gateway, e i conti si connettono col nuovo nome."
        >
          {(id) => (
            <input
              id={id}
              required
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setSuggestions([]);
              }}
              className={inputClass}
            />
          )}
        </Field>
        <NameSuggestions
          names={suggestions}
          onPick={(s) => {
            setName(s);
            setSuggestions([]);
            setError(null);
          }}
        />
        {nameChanged && !shownPreview && <p className="text-xs text-faint">Calcolo le conseguenze…</p>}
        {shownPreview && shownPreview.data === null && (
          <p role="status" className="rounded border border-warn/40 bg-warn/10 px-3 py-2 text-xs text-warn">
            Anteprima delle conseguenze non disponibile: non so quanti conti userebbero il nuovo nome. Il salvataggio resta possibile.
          </p>
        )}
        {shownPreview?.data && (shownPreview.data.consequences.length > 0 || blockers.length > 0) && (
          <div role="status" className="space-y-1 rounded border border-warn/40 bg-warn/10 px-3 py-2 text-sm text-warn">
            {blockers.map((b) => (
              <p key={b.code} className="text-loss">
                {b.message}
              </p>
            ))}
            {shownPreview.data.consequences.map((c) => (
              <p key={c.code}>{c.label}</p>
            ))}
          </div>
        )}

        <Field label="Nome visualizzato" hint="Facoltativo: come si legge nelle liste al posto del nome MT5. Vuoto = si usa il nome MT5.">
          {(id) => <input id={id} value={displayName} onChange={(e) => setDisplayName(e.target.value)} className={inputClass} />}
        </Field>

        <Field
          label={kind === "PROP" ? "Prop firm" : "Broker"}
          hint="Cambiare il possessore sposta il server da una scheda all'altra. Non tocca conti, simboli né fuso."
        >
          {(id) => (
            <select id={id} value={ownerId} onChange={(e) => setOwnerId(e.target.value)} className={inputClass}>
              <option value="">Nessun possessore</option>
              {ownerArchived && server.propFirm && <option value={server.propFirm.id}>{server.propFirm.name} (archiviato)</option>}
              {owners.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          )}
        </Field>
        <ErrorNote>{error}</ErrorNote>
      </form>
    </SidePanel>
  );
}
